import { PlayerRatingScope } from 'components/ReviewAudit/PublicRatings'
import ParticipantOption from '../../components/ParticipantOption'
import EditorActions from 'components/Common/EditorActions'
import modalSelectMenuProps from "components/Common/modalSelectMenuProps"
import React, { useEffect, useMemo, useRef, useState } from "react"
import Select from "react-select"
import { debounce, isEmpty } from "lodash"
import {
  Button,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import { Form, Formik } from "formik"
import * as Yup from "yup"
import { getRegistrationQuote } from "../../../../../api"
import { admissionRefusal, isLevel, quoteRefusal } from "./entryAdmissionText"
import { knownEntryMessage } from "./useEntryOperation"

const MIN_CHARS = 3
const QUOTE_UNAVAILABLE = "Unable to load the Sportya fee quote. Check the current selection and try again."
const QUOTE_CHANGED = "The competition or the payer's wallet changed after the fee quote was loaded. Review the updated quote below, then select Add again."

const competitionEntry = Yup.object().shape({})

const formatAmount = (amount, currency) => `${amount} ${currency}`

const participantName = participant =>
  `${participant.firstName} ${participant.lastName}`

const AddEntryModal = ({
  competitionId,
  eventId,
  gameType,
  level,
  restrictions,
  entryLimit,
  isOpen,
  loadingResults,
  searchError = false,
  onRetrySearch,
  onClose,
  onSearch,
  onSubmit,
  options,
  slotType = "STANDARD",
  pending = false,
  recovery,
  disabledReason,
  operationError,
  onRetryAvailability,
}) => {
  const [selectedPlayer, setSelectedPlayer] = useState({})
  const [selectedPartner, setSelectedPartner] = useState({})
  const [reason, setReason] = useState("")
  const [quote, setQuote] = useState(null)
  const [quoteSelectionKey, setQuoteSelectionKey] = useState(null)
  const [quoteStatus, setQuoteStatus] = useState("idle")
  const [quoteAttempt, setQuoteAttempt] = useState(0)
  const [quoteError, setQuoteError] = useState({})
  // The search data every Check again was asked with. A level set in a player profile meanwhile does not show in it, so a
  // level refusal for that pick no longer names a level; a player searched and picked again brings fresh data (CP-5).
  const [recheckedData, setRecheckedData] = useState([])

  const payerUserId = selectedPlayer?.data?.userId
  const partnerUserId = selectedPartner?.data?.userId
  const doublesLevels = [
    selectedPlayer?.data?.gameLevel?.doubles,
    selectedPartner?.data?.gameLevel?.doubles,
  ]
  // Only two known levels can be too far apart; a missing one goes to the quote, which names it (CP-5).
  const exceedsDoublesLevelGap =
    gameType === "doubles" && doublesLevels.every(isLevel) && Math.abs(doublesLevels[0] - doublesLevels[1]) > 2
  const completeTeam =
    Boolean(payerUserId) &&
    (gameType !== "doubles" || Boolean(partnerUserId)) &&
    payerUserId !== partnerUserId &&
    !exceedsDoublesLevelGap
  const selectionKey = [
    eventId,
    competitionId,
    gameType,
    slotType,
    payerUserId,
    gameType === "doubles" ? partnerUserId : "",
  ].join(":")
  const hasCurrentQuote =
    quoteStatus === "success" && quoteSelectionKey === selectionKey
  const reasonMissing = hasCurrentQuote && quote?.reasonRequired && !reason.trim()
  // A late add offers a note to everyone; only an API-required reason blocks Add (DEC-2).
  const offersNote = hasCurrentQuote && Boolean(quote?.outsideRegistrationPeriod || quote?.reasonRequired)
  const refusalContext = { gameType, level, restrictions, player: selectedPlayer?.data, partner: selectedPartner?.data }
  const pickedData = [selectedPlayer?.data, selectedPartner?.data].filter(Boolean)
  const levelsRechecked = pickedData.some(data => recheckedData.includes(data))
  // A quote refusal names its cause and never offers Retry quote: only another selection fits, or a fix made elsewhere, which
  // "Check again" re-quotes. Retry quote stays for what a retry can change.
  const refusal = quoteStatus === "error" && quoteError.refused
    ? quoteRefusal(quoteError.code, { ...refusalContext, entryLimit, participant: quoteError.participant, levelsRechecked })
    : null
  // A 5xx keeps the quote's own words, whatever code it carries.
  const quoteErrorText = refusal?.text || (quoteError.refused && knownEntryMessage(quoteError.code)) || QUOTE_UNAVAILABLE
  const quoteErrorAction = refusal ? refusal.recheck && "Check again" : "Retry quote"

  // `operationError` is the failed add, `{ id, code, message, refused }`, or null. It belongs to the opening that sent it: the
  // modal stays mounted between openings, so the opening is detected here, during render, and an error already there when the
  // dialog opens is never shown, not even for one render.
  const wasOpen = useRef(false)
  const openedWith = useRef(undefined)
  if (isOpen && !wasOpen.current) openedWith.current = operationError?.id
  wasOpen.current = isOpen
  const shown = operationError && operationError.id !== openedWith.current ? operationError : null
  // An Events refusal reloads the quote, once per operation. This is an event, never a dependency of the quote effect:
  // operationError returns to null as soon as the next Add starts. A send the gateway refused never reached Events, so
  // its words stand as they are and the quote is not read again (it would meet the same gate).
  const requoted = useRef(null)
  const requoteFor = shown && !shown.refused ? shown.id : null
  useEffect(() => {
    if (!requoteFor || requoteFor === requoted.current) return
    requoted.current = requoteFor
    setQuoteAttempt(attempt => attempt + 1)
  }, [requoteFor])
  // A reloaded quote refused for the same reason says it once, with the person's name.
  const quoteRepeats = Boolean(shown && !shown.refused && shown.code) && quoteStatus === "error" && quoteError.code === shown.code
  const operationErrorText = shown && !quoteRepeats && (!shown.refused && shown.code === "ENTRY_PREVIEW_CHANGED" ? QUOTE_CHANGED : shown.message)

  useEffect(() => {
    setQuote(null)
    setQuoteSelectionKey(null)

    if (!isOpen || !eventId || !competitionId || !completeTeam) {
      setQuoteStatus("idle")
      return
    }

    setQuoteStatus("loading")
    let requestIsCurrent = true

    const loadQuote = async () => {
      try {
        const response = await getRegistrationQuote({
          eventId,
          competitionId,
          payerUserId,
          ...(slotType === "WILD_CARD" && { slotType }),
          ...(gameType === "doubles" && { partnerUserId }),
        })

        if (!requestIsCurrent) return

        if (!response?.data) {
          throw new Error("Registration quote is unavailable")
        }

        setQuote(response.data)
        setQuoteSelectionKey(selectionKey)
        setQuoteStatus("success")
      } catch (error) {
        if (requestIsCurrent) {
          const { status, data: answer } = error.response || {}
          // Only a 4xx is the API's answer about this selection; a 5xx or a lost request may pass on retry.
          setQuoteError({ code: answer?.message || "", participant: answer?.data?.participant, refused: status >= 400 && status < 500 })
          setQuoteStatus("error")
        }
      }
    }

    loadQuote()

    return () => {
      requestIsCurrent = false
    }
  }, [
    competitionId,
    eventId,
    gameType,
    slotType,
    isOpen,
    payerUserId,
    partnerUserId,
    completeTeam,
    selectionKey,
    quoteAttempt,
  ])

  // The draft (players and note) belongs to one opening: a confirmed add closes the
  // dialog from outside, so the next Add entry or Add wild card must start empty.
  useEffect(() => {
    if (isOpen) return
    setReason("")
    setSelectedPlayer({})
    setSelectedPartner({})
    setQuoteAttempt(0)
    setQuoteError({})
    setRecheckedData([])
  }, [isOpen])

  const closeModal = () => {
    if (pending) return
    onClose()
  }

  // Every pick is kept, as the control shows it: a suspended or level-less player is quoted, and the quote says why the API
  // refuses them (CP-5).
  const selectParticipant = setter => option => setter(option || {})

  const teamIsIncomplete =
    isEmpty(selectedPlayer) ||
    (gameType === "doubles" && isEmpty(selectedPartner))
  const teamHasDuplicate =
    selectedPlayer?.data?.userId === selectedPartner?.data?.userId

  const noOptionsMessage = ({ inputValue }) => (
    <>
      {searchError && <>Player search is unavailable. Please retry.</>}
      {!searchError && inputValue.length >= MIN_CHARS && !loadingResults && (
        <>No results found.</>
      )}
      {inputValue.length < MIN_CHARS && <>Write at least 3 characters...</>}
      {loadingResults && <>Loading...</>}
    </>
  )

  const searchParticipants = useMemo(() => debounce((value, meta) => {
    if (meta?.action && meta.action !== "input-change") return
    if (value?.length >= MIN_CHARS) {
      onSearch(value)
    }
  }, 100), [onSearch])
  useEffect(() => () => searchParticipants.cancel(), [searchParticipants])

  return (
    <PlayerRatingScope players={isOpen ? [...(options || []).map(option => option.data), selectedPlayer.data, selectedPartner.data] : []}><Modal size="lg" scrollable isOpen={isOpen} toggle={closeModal}>
      <Formik
        initialValues={{}}
        validationSchema={competitionEntry}
        onSubmit={() =>
          !disabledReason && !pending && hasCurrentQuote && !reasonMissing && onSubmit({ player: selectedPlayer, partner: selectedPartner, quote, reason: offersNote ? reason.trim() : "" })
        }
      >
        <Form>
          <ModalHeader toggle={closeModal}>{slotType === "WILD_CARD" ? "Add wild card" : "Add entry"}</ModalHeader>
          <ModalBody>
            {disabledReason && <div role="alert" className="alert alert-danger">{disabledReason}
              {onRetryAvailability && <Button color="link" type="button" onClick={onRetryAvailability}>Retry entry availability</Button>}
            </div>}
            {operationErrorText && <div role="alert" className="alert alert-danger">{operationErrorText}</div>}
            {slotType === "WILD_CARD" && <p className="text-muted">Uses one available reserved WC place in the Main List. The applicable Sportya fee is shown below before saving.</p>}
            {searchError && <div role="alert" className="alert alert-danger">
              Player search is unavailable. Try again; your selection has been kept.
              {onRetrySearch && <Button type="button" color="link" onClick={onRetrySearch}>Retry player search</Button>}
            </div>}
            <FormGroup>
              <Label>Player (payer)</Label>
              <p className="text-muted small mb-2">
                The first player is the payer. Their wallet is charged the full
                team Sportya fee in their currency.
              </p>
              <Select
                        formatOptionLabel={ParticipantOption}
                {...modalSelectMenuProps}
                menuPortalTarget={document.body}
                placeholder="Search for player..."
                options={options}
                isOptionSelected={(option, selectValue) =>
                  selectValue.some(item => item === option)
                }
                onInputChange={searchParticipants}
                onChange={selectParticipant(setSelectedPlayer)}
                noOptionsMessage={noOptionsMessage}
              />
            </FormGroup>
            {gameType === "doubles" && (
              <FormGroup>
                <Label>Partner</Label>
                <Select
                        formatOptionLabel={ParticipantOption}
                  {...modalSelectMenuProps}
                  menuPortalTarget={document.body}
                  placeholder="Search for partner..."
                  options={options}
                  isOptionSelected={(option, selectValue) =>
                    selectValue.some(item => item === option)
                  }
                  onInputChange={searchParticipants}
                  onChange={selectParticipant(setSelectedPartner)}
                  noOptionsMessage={noOptionsMessage}
                />
              </FormGroup>
            )}
            {quoteStatus === "loading" && (
              <p className="text-muted mb-0">Loading Sportya fee quote...</p>
            )}
            {exceedsDoublesLevelGap && (
              <div className="alert alert-danger mb-0">
                {admissionRefusal("TEAM_NOT_ALLOWED_AVG_LEVEL", refusalContext)}
              </div>
            )}
            {quoteStatus === "error" && (
              <div className="alert alert-danger mb-0">
                <p className={quoteErrorAction ? "mb-2" : "mb-0"}>
                  {quoteErrorText}
                </p>
                {quoteErrorAction && <Button
                  className="p-0"
                  color="link"
                  onClick={() => {
                    // Check again (a refusal's action), not Retry quote.
                    if (refusal) setRecheckedData(rechecked => [...rechecked, ...pickedData])
                    setQuoteAttempt(attempt => attempt + 1)
                  }}
                  type="button"
                >
                  {quoteErrorAction}
                </Button>}
              </div>
            )}
            {hasCurrentQuote && quote && (
              <div className="border rounded bg-light p-3">
                {quote.participants.map(participant => (
                  <div
                    className="d-flex justify-content-between mb-2"
                    key={`${participant.role}-${participant.userId}`}
                  >
                    <span>
                      {participant.role === "payer" ? "Payer" : "Partner"}: {" "}
                      {participantName(participant)}
                    </span>
                    <strong>
                      {formatAmount(participant.fee, quote.currency)}
                    </strong>
                  </div>
                ))}
                <div className="d-flex justify-content-between mb-2"><span>Destination</span><strong>{{ mainList: "Main List", waitingList: "Waiting List", preRegistrationList: "Sorting List" }[quote.destination] || "Not available"}</strong></div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between mb-2">
                  <span>Entry currency</span>
                  <strong>{quote.currency}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Team Sportya total</span>
                  <strong>{formatAmount(quote.total, quote.currency)}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Payer wallet balance</span>
                  <strong>
                    {formatAmount(quote.walletBalance, quote.currency)}
                  </strong>
                </div>
                {quote.canPay && (
                  <p className="alert alert-success mt-3 mb-0">
                    {formatAmount(quote.total, quote.currency)} will be debited
                    from {participantName(quote.payer)}&apos;s wallet when this
                    entry is added.
                  </p>
                )}
                {!quote.canPay && (
                  <p className="alert alert-warning mt-3 mb-0">
                    {participantName(quote.payer)}&apos;s wallet cannot cover {" "}
                    {formatAmount(quote.total, quote.currency)}. You can still add this entry as Unpaid.
                    {" "}No money will be debited; the outstanding Sportya fee must be collected separately.
                  </p>
                )}
              </div>
            )}
            {offersNote && <FormGroup className="mt-3">
              <Label for="entry-add-reason">{quote.reasonRequired ? "Reason (required)" : "Note (optional)"}</Label>
              <textarea id="entry-add-reason" aria-describedby="entry-add-reason-help" className="form-control" required={Boolean(quote.reasonRequired)} maxLength={2000} value={reason} onChange={event => setReason(event.target.value)} disabled={pending} />
              <small id="entry-add-reason-help" className="text-muted">{quote.reasonRequired
                ? "This registration is outside the normal period. A reason is required and will be saved in the action history."
                : "Optional. Saved with this entry and shown in Action Logs."}</small>
            </FormGroup>}
          </ModalBody>
          <ModalFooter><EditorActions dirty={!isEmpty(selectedPlayer) || !isEmpty(selectedPartner) || Boolean(reason)} pending={pending} saveLabel="Add"
            validationMessage={reasonMissing ? "Enter a reason to enable Add." : undefined}>
            {recovery}
            <Button color="secondary" disabled={pending} onClick={closeModal}>
              Cancel
            </Button>
            <Button
              disabled={
                teamHasDuplicate ||
                teamIsIncomplete ||
                !hasCurrentQuote || pending || Boolean(disabledReason) || reasonMissing
              }
              type="submit"
              color="primary"
            >
              Add
            </Button>
          </EditorActions></ModalFooter>
        </Form>
      </Formik>
    </Modal></PlayerRatingScope>
  )
}

export default AddEntryModal
